import { useContext, useEffect, useState } from 'react';
import { MaskType } from '@givebutter/primitives/input';
import { maskDecimal, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { IconDefinition, faCalendar, faDollar, faText } from '@fortawesome/pro-regular-svg-icons';
import { faInputNumeric } from '@fortawesome/pro-solid-svg-icons';
import { FilterProviderContext } from 'components/filter/filters-next/FilterProvider';
import { useFilterModulesStore } from 'components/filter/filters-next/filterModules';
import * as DateFilter from 'components/filter/filters-next/filters/Date';
import * as TextFilter from 'components/filter/filters-next/filters/Text';
import {
    getColumnFilterKey,
    prefixDynamicFilter,
    transformValueToRequest,
} from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    DownstreamPreprocessTransform,
    FilterModuleStore,
    FilterOperator,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import { useReportSchemas } from 'customHooks/useReportSchemas';
import { identity } from 'lodash';
import { twJoin } from 'tailwind-merge';
import { ColumnSchema, ColumnSchemaFilter, ResourceSchema } from 'types/domain';

type RegisterFn = FilterModuleStore['register'];
type RegisterFilterConfig = (
    props: ColumnFormatProps,
) => Omit<Parameters<RegisterFn>[0], 'filterKey'>;

const useRegisterFilters = () => {
    const { modules, register } = useFilterModulesStore();
    const { data: schemas } = useReportSchemas();
    const [didInit, setDidInit] = useState(false);

    useEffect(() => {
        const registerColumn = (module: ColumnSchema, schema: ResourceSchema) => {
            const namespacedKey = prefixDynamicFilter(module.name as ServerKey);
            registerModule(module, namespacedKey, register);

            module.column_filters.forEach(filter => {
                const computedColumn = columnFilterFactory(filter, module);
                const namespacedKey = getColumnFilterKey(
                    computedColumn.name,
                    filter.type,
                    schema.resource,
                );

                const overrideConfig: RegisterFilterConfig = ({ ReadableComponent, mask }) => ({
                    Readable: () => (
                        <ReadableComponent
                            mask={mask}
                            label={<ColumnFilterReadable filter={filter} parentSchema={module} />}
                        />
                    ),
                });

                registerModule(computedColumn, namespacedKey, register, overrideConfig);
            });
        };

        schemas.forEach(schema =>
            schema.schema.columns.forEach(module => registerColumn(module, schema)),
        );
        setDidInit(true);
    }, [schemas, modules, register]);

    return { didInit };
};

const registerModule = (
    module: ColumnSchema,
    namespacedKey: ServerKey,
    register: RegisterFn,
    config?: RegisterFilterConfig,
) => {
    const Label = <span className={'block max-w-48 truncate'}>{module.label}</span>;
    const columnProps = getModulePropsByColumnFormat(module);
    const { FilterComponent, ReadableComponent, ...moduleProps } = columnProps;

    register({
        filterKey: namespacedKey,
        Filter: () => <FilterComponent maskType={moduleProps.maskType} label={Label} />,
        Readable: () => <ReadableComponent mask={moduleProps.mask} />,
        Label: () => Label,
        Icon: moduleProps.icon,
        downstream: moduleProps.downstream,
        upstream: moduleProps.upstream,
        preprocessDownstream: moduleProps.preprocessDownstream,
        ...config?.(columnProps),
    });
};

const columnFilterFactory = (
    filter: ColumnSchemaFilter,
    parentSchema: ColumnSchema,
): ColumnSchema => {
    const column = {
        ...parentSchema,
        format: null,
        label: filter.label,
    };

    switch (filter.type) {
        case 'comparison':
            return {
                ...column,
                value_type: 'alpha',
            };
        case 'between':
        case 'date_range':
            return {
                ...column,
                value_type: 'date',
            };
        case 'model_filter':
            return parentSchema;
    }
};

interface ColumnFormatProps {
    maskType: MaskType | null;
    mask: ((value: string) => string) | null;
    unmask: (value: string) => number;
    icon: IconDefinition;
    FilterComponent: ContentFilterComponent;
    ReadableComponent: ReadableFilterComponent;
    upstream: UpstreamFilterTransform<any>;
    downstream: DownstreamFilterTransform<any>;
    preprocessDownstream?: DownstreamPreprocessTransform<any>;
}

const getModulePropsByColumnFormat = (column: ColumnSchema): ColumnFormatProps => {
    const props: ColumnFormatProps = {
        maskType: null,
        mask: null,
        unmask: identity,
        downstream: identity,
        upstream: identity,
        icon: faText,
        FilterComponent: TextFilter.Filter,
        ReadableComponent: TextFilter.Readable,
    };

    switch (column.value_type) {
        case 'numeric':
            if (column.format === 'currency') {
                props.maskType = 'decimal';
                props.mask = maskDecimal;
                props.unmask = unmaskNumber;
                props.icon = faDollar;
            } else {
                props.maskType = 'whole-number';
                props.mask = maskWholeNumber;
                props.unmask = unmaskNumber;
                props.icon = faInputNumeric;
            }
            props.downstream = props.mask;
            props.upstream = filter =>
                transformValueToRequest<string, number>(
                    filter.value as string,
                    'and',
                    filter.filterOperator as FilterOperator,
                    props.unmask,
                );
            break;
        case 'date':
            props.icon = faCalendar;
            props.FilterComponent = DateFilter.Filter;
            props.ReadableComponent = DateFilter.Readable;
            props.downstream = DateFilter.downstream;
            props.preprocessDownstream = DateFilter.preprocessDownstream;
            props.upstream = DateFilter.upstream;
            break;
        case 'alpha':
            break;
    }

    return props;
};

interface ColumnFilterReadableProps {
    filter: ColumnSchemaFilter;
    parentSchema: ColumnSchema;
}

const ColumnFilterReadable = ({ filter, parentSchema }: ColumnFilterReadableProps) => {
    const { cacheKey } = useContext(FilterProviderContext);
    const isTableBar = cacheKey === 'tableFiltersBar';

    return (
        <span className={'flex gap-1'}>
            {isTableBar && <span className={'text-gray-900'}>{parentSchema.label}:</span>}
            <span className={twJoin(isTableBar && 'lowercase')}>{filter.label}</span>
        </span>
    );
};

export { useRegisterFilters };
